import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  computed,
  inject,
  OnInit,
  Renderer2,
  signal,
  OnDestroy,
  viewChild,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ToastContainerComponent } from 'nebula-ui-kit';
import {
  SidebarStateService,
  RouteSyncService,
  PROFILE_MENU,
  BreadcrumbSyncService,
} from '@nebula-layout/layout';
import { BreadcrumbItem } from 'nebula-ui-kit';
import { BreadcrumbWrapperComponent } from '../breadcrumb-wrapper/breadcrumb-wrapper.component';
import { FooterWrapperComponent } from '../footer-wrapper/footer-wrapper.component';
import { VAULT_MENU_ITEMS } from '../../../../../const/src/lib/layout.constants';
import { filter, Subscription } from 'rxjs';
import { SidebarWrapperComponent2 } from '../sidebar-wrapper/sidebar-wrapper.component';
import { TopbarWrapperComponent2 } from '../topbar-wrapper/topbar-wrapper.component';
import {
  AuthService,
  VaultAppToastService,
  VaultAccountBlockedService,
  VaultMenuAccessService,
  VaultOperativeRoleSyncService,
  VaultTenantSessionBootstrapService,
  VaultSessionInactivityService,
  syncVaultSuperadminInStoredUserData,
} from '@nebula/shared-vault/data-access';

@Component({
  selector: 'app-layout',
  templateUrl: './layout-wrapper.component.html',
  styleUrls: ['./layout-wrapper.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    SidebarWrapperComponent2,
    TopbarWrapperComponent2,
    BreadcrumbWrapperComponent,
    FooterWrapperComponent,
    ToastContainerComponent,
  ],
})
export class LayoutWrapperComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly renderer = inject(Renderer2);
  readonly breakpointObserver = inject(BreakpointObserver);
  private readonly authService = inject(AuthService);
  private readonly accountBlockedService = inject(VaultAccountBlockedService);
  /** Inicializa control de inactividad (24 h + aviso 5 min, alineado al legacy). */
  private readonly _sessionInactivity = inject(VaultSessionInactivityService);
  private readonly vaultMenuAccess = inject(VaultMenuAccessService);
  private readonly vaultAppToast = inject(VaultAppToastService);
  private readonly vaultOperativeRoleSync = inject(VaultOperativeRoleSyncService);
  private readonly vaultTenantBootstrap = inject(VaultTenantSessionBootstrapService);
  private readonly vaultToastContainer = viewChild<ToastContainerComponent>('vaultToastContainer');
  private vaultAccessToastSub?: Subscription;
  private vaultAppToastSub?: Subscription;
  showSidebar = false;
  showHeader = false;
  showBreadcrumb = false;
  isSidebarCollapsed = false;
  isDarkMode = signal<boolean>(false);
  userName = signal<string>('');
  readonly subs = new Subscription();

  readonly sidebarState = inject(SidebarStateService);
  readonly router = inject(Router);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly routeSync = inject(RouteSyncService);
  readonly breadcrumbSync = inject(BreadcrumbSyncService);

  breadcrumbItems = signal<BreadcrumbItem[]>([]);

  itemsProfileMenu = signal<Array<PROFILE_MENU>>(VAULT_MENU_ITEMS);

  ngOnInit(): void {
    this.subs.add(
      this.vaultTenantBootstrap.ensureSession$({ force: true }).subscribe({
        next: () => syncVaultSuperadminInStoredUserData(),
        error: () => {
          /* Cuenta inactiva/eliminada: VaultAccountBlockedService redirige al login con toast. */
        },
      }),
    );
    this.updateLayoutFromRoute();

    this.subs.add(
      this.breadcrumbSync.breadcrumb$.subscribe((items) => {
        this.breadcrumbItems.set(items?.length ? items : []);
      }),
    );

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateLayoutFromRoute();
    });

    this.subs.add(
      this.sidebarState.isExpanded$.subscribe((expanded) => {
        this.isSidebarCollapsed = !expanded;
      }),
    );

    const savedDarkMode = this.havesaveDarkMode();
    this.isDarkMode.set(savedDarkMode);
    this.userName.set(this.user());

    this.applyTheme(savedDarkMode);
  }

  ngAfterViewInit(): void {
    this.vaultAccessToastSub = this.vaultMenuAccess.accessDenied$.subscribe((event) => {
      if (!event?.message) return;
      if (this.authService.isVaultSuperadmin() || this.authService.isVaultAdminEmpresa()) {
        this.vaultMenuAccess.clearAccessDenied();
        return;
      }
      const routerUrl = this.router.url.split('?')[0];
      if (routerUrl.includes('/layout/vault/access-denied')) {
        this.vaultMenuAccess.clearAccessDenied();
        return;
      }
      this.vaultToastContainer()?.show({
        type: 'warning',
        title: 'Acceso restringido',
        message: event.message,
        autoClose: true,
        duration: 2800,
        showCloseButton: true,
      });
      this.vaultMenuAccess.clearAccessDenied();
    });

    this.vaultAppToastSub = this.vaultAppToast.toast$.subscribe((event) => {
      this.vaultToastContainer()?.show({
        type: event.type,
        title: event.title,
        message: event.message,
        autoClose: true,
        duration: event.duration ?? 3200,
        showCloseButton: true,
      });
    });
  }

  onMenuItemClick(action: string): void {
    if (action === 'Cerrar sesión') {
      this.logout();
    }
  }

  applyTheme(isDark: boolean): void {
    const htmlElement = document.documentElement;

    if (isDark) {
      this.renderer.addClass(htmlElement, 'dark-theme');
    } else {
      this.renderer.removeClass(htmlElement, 'dark-theme');
    }
  }

  updateLayoutFromRoute(): void {
    let route = this.activatedRoute.snapshot.firstChild;

    while (route?.firstChild) {
      route = route.firstChild;
    }

    if (route?.data) {
      this.showSidebar = route.data['showSidebar'] ?? true;
      this.showHeader = route.data['showHeader'] ?? true;
      this.showBreadcrumb = route.data['showBreadcrumb'] ?? true;
    } else {
      this.showSidebar = true;
      this.showHeader = true;
      this.showBreadcrumb = true;
    }
  }

  onSidebarCollapsedChange(collapsed: boolean): void {
    this.isSidebarCollapsed = collapsed;
    this.sidebarState.setState(!collapsed, this.showSidebar);
  }

  get isConnected(): boolean {
    if (typeof window === 'undefined') return false;
    const userData = localStorage.getItem('user_data');
    return userData !== null && userData !== undefined && userData !== '';
  }

  private havesaveDarkMode(): boolean {
    const darkMode = localStorage.getItem('dark_mode');
    return darkMode === 'true';
  }

  darkmodeChange() {
    const newDarkMode = !this.isDarkMode();
    this.isDarkMode.set(newDarkMode);
    localStorage.setItem('dark_mode', newDarkMode.toString());
    this.applyTheme(newDarkMode);
  }

  logout(): void {
    this.accountBlockedService.clear();
    this.authService.logout();
  }

  private user(): string {
    const DATA_USER = localStorage.getItem('user_data');
    if (!DATA_USER) return '';
    const JSON_DATA_USER = JSON.parse(DATA_USER);
    return JSON_DATA_USER.username;
  }

  get footerVariant(): 'default' | 'login' {
    return this.router.url.includes('/login') ? 'login' : 'default';
  }

  isTablet = computed(() =>
    this.breakpointObserver.isMatched('(min-width: 768px) and (max-width: 1024px)'),
  );

  readonly logoUrl = computed(() => {
    const isDark = this.isDarkMode();
    const isTablet = this.isTablet();

    const basePath = isDark
      ? 'assets/shell/logos/dark/logo-nebula-vault-dark'
      : 'assets/shell/logos/light/logo-nebula-vault-light';

    return `${basePath}${isTablet ? '-tablet' : ''}.svg`;
  });

  ngOnDestroy(): void {
    this.vaultAccessToastSub?.unsubscribe();
    this.vaultAppToastSub?.unsubscribe();
    this.subs.unsubscribe();
  }
}
